import { useState, useRef } from "react";
import { useImportElectoralMetadataCsv } from "../api/electoral-metadata/hooks";
import type { ImportValidationErrorDto } from "../api/electoral-metadata/types";
import { showError, showSuccess } from "../shared/toast";
import "./import-metadata-dialog.css";

interface ImportMetadataDialogProps {
  open: boolean;
  municipalityId: string;
  onClose: () => void;
}

export const ImportMetadataDialog = ({
  open,
  municipalityId,
  onClose
}: ImportMetadataDialogProps): JSX.Element | null => {
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<ImportValidationErrorDto[] | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const importMutation = useImportElectoralMetadataCsv(municipalityId || null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    setFile(f ?? null);
    setReport(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !municipalityId) return;
    setReport(null);
    try {
      const result = await importMutation.mutateAsync(file);
      if (result.success) {
        const msg =
          result.rowsImported > 0 || result.cellsUpdated > 0
            ? `${result.rowsImported} rader, ${result.cellsUpdated} celler uppdaterade.`
            : "Metadata har importerats.";
        showSuccess(msg ? `Import lyckades. ${msg}` : "Import lyckades.");
        setFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        onClose();
      } else {
        setReport(result.report.errors);
      }
    } catch (err: unknown) {
      const msg =
        (err as { message?: string })?.message ?? "Import misslyckades.";
      showError("Import misslyckades", msg);
    }
  };

  const handleClose = () => {
    setFile(null);
    setReport(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    onClose();
  };

  if (!open) return null;

  return (
    <div
      className="import-metadata-dialog-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="import-metadata-dialog-title"
    >
      <div className="import-metadata-dialog">
        <h2 id="import-metadata-dialog-title">Importera valdistriktsmetadata</h2>
        <p className="import-metadata-dialog__description">
          Ladda upp en CSV-fil. Kolumnerna ska motsvara metadatafälten. Vid
          valideringsfel visas en rapport nedan.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="import-metadata-dialog__field">
            <label htmlFor="import-metadata-file">CSV-fil</label>
            <input
              id="import-metadata-file"
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              aria-required="true"
            />
          </div>
          {report && report.length > 0 && (
            <div className="import-metadata-dialog__report">
              <h3>Valideringsfel</h3>
              <table className="import-metadata-report-table">
                <thead>
                  <tr>
                    <th scope="col">Rad</th>
                    <th scope="col">Kolumn</th>
                    <th scope="col">Meddelande</th>
                  </tr>
                </thead>
                <tbody>
                  {report.map((err, i) => (
                    <tr key={i}>
                      <td>{err.row ?? "—"}</td>
                      <td>{err.column ?? "—"}</td>
                      <td>{err.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="import-metadata-dialog__actions">
            <button
              type="button"
              className="button button--secondary"
              onClick={handleClose}
            >
              Stäng
            </button>
            <button
              type="submit"
              className="button button--primary"
              disabled={!file || importMutation.isPending}
            >
              {importMutation.isPending ? "Importerar..." : "Importera"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
